// A small synthetic world for the hero simulation. Nothing here is customer data.
// It is modelled on the scenario of crates/yocho-simulator (docs/yocho-simulator.md): an
// overt churner, a silent churner (slow reply-latency drift, a quieter sponsor, narrowing
// orders), a decoy that looks bad but stays (a one-off outage burst) and a healthy account.
// Input names are Yochō signal names; the weights are illustrative, not a shipped template.

export const DAYS = 120;
export const ALERT_AT = 60; // start of the "high" risk band (docs/yocho-public-api.md)
export const SUSTAIN_DAYS = 3; // illustrative persistence rule

export const INPUTS = [
  { key: 'email.time_to_first_response', short: 'reply latency', w: 0.18 },
  { key: 'email.open_unanswered_count', short: 'unanswered', w: 0.10 },
  { key: 'email.sentiment', short: 'sentiment drift', w: 0.12 },
  { key: 'email.urgency', short: 'urgency', w: 0.12 },
  { key: 'ticket.sla_breaches', short: 'SLA breaches', w: 0.12 },
  { key: 'commitment.broken', short: 'broken promises', w: 0.12 },
  { key: 'order.cadence_ratio', short: 'order cadence', w: 0.16 },
  { key: 'billing.days_past_due', short: 'days past due', w: 0.08 },
];

export const SOURCES = [
  { key: 'email', label: 'Email', sub: 'Graph + transport', color: '#8fb4ff' },
  { key: 'zendesk', label: 'Zendesk', sub: 'tickets', color: '#5fe0c1' },
  { key: 'teams', label: 'Teams', sub: 'chat', color: '#c3a6ff' },
  { key: 'logs', label: 'Logs', sub: 'generic', color: '#9aa7bd' },
  { key: 'erp', label: 'ERP / AR', sub: 'orders, invoices', color: '#ffc27a' },
];

// Which inputs an arriving item can move, per source. Teams and Logs flow through the
// platform as normalized events; they do not feed the health template here.
export const SOURCE_INPUTS = {
  email: ['email.time_to_first_response', 'email.open_unanswered_count', 'email.sentiment', 'email.urgency', 'commitment.broken'],
  zendesk: ['ticket.sla_breaches', 'email.urgency', 'commitment.broken'],
  erp: ['order.cadence_ratio', 'billing.days_past_due'],
  teams: [],
  logs: [],
};

const LABELS = {
  'email.time_to_first_response': ['reply latency ↑', 'reply latency ↓'],
  'email.open_unanswered_count': ['unanswered +1', 'thread answered'],
  'email.sentiment': ['sentiment ↓', 'sentiment ↑'],
  'email.urgency': ['urgency: billing dispute', 'urgency eased'],
  'ticket.sla_breaches': ['SLA breach', 'ticket solved'],
  'commitment.broken': ['promise broken (re-ask)', 'promise kept'],
  'order.cadence_ratio': ['order cadence ↓', 'order on cadence'],
  'billing.days_past_due': ['days past due ↑', 'invoice paid'],
};
export const labelFor = (key, rising) => LABELS[key][rising ? 0 : 1];

// Piecewise-linear risk (0..1) per input over the simulated days.
const K = (...pts) => pts;
export const CUSTOMERS = [
  {
    id: 'cust-0007', name: 'Arden Supply', short: 'Arden', bu: 'Industrial',
    archetype: 'silent churner', churnDay: 112,
    volume: { email: 0.9, zendesk: 0.35, teams: 0.25, logs: 0.1, erp: 0.45 },
    quietFrom: 60, // the sponsor goes quiet: less mail as the account drifts
    inputs: {
      'email.time_to_first_response': K([0, 0.1], [35, 0.12], [95, 0.95], [120, 1]),
      'email.open_unanswered_count': K([0, 0.1], [45, 0.15], [100, 0.9], [120, 0.9]),
      'email.sentiment': K([0, 0.15], [60, 0.2], [110, 0.6]),
      'email.urgency': K([0, 0.05], [80, 0.1], [110, 0.3]),
      'ticket.sla_breaches': K([0, 0.05], [90, 0.1], [115, 0.3]),
      'commitment.broken': K([0, 0.05], [70, 0.1], [100, 0.6]),
      'order.cadence_ratio': K([0, 0.05], [50, 0.1], [100, 1], [120, 1]),
      'billing.days_past_due': K([0, 0.05], [85, 0.1], [115, 0.4]),
    },
  },
  {
    id: 'cust-0012', name: 'Brightwater Labs', short: 'Brightwater', bu: 'Life sciences',
    archetype: 'decoy: one-off outage', churnDay: null,
    volume: { email: 0.8, zendesk: 0.4, teams: 0.4, logs: 0.2, erp: 0.5 },
    burst: [66, 72],
    inputs: {
      'email.time_to_first_response': K([0, 0.12], [65, 0.12], [68, 0.95], [70, 0.5], [76, 0.15]),
      'email.open_unanswered_count': K([0, 0.1], [65, 0.1], [68, 0.95], [70, 0.5], [76, 0.1]),
      'email.sentiment': K([0, 0.15], [65, 0.15], [68, 0.85], [71, 0.6], [80, 0.2]),
      'email.urgency': K([0, 0.05], [65, 0.05], [67, 1], [70, 0.8], [75, 0.05]),
      'ticket.sla_breaches': K([0, 0.05], [65, 0.05], [67, 1], [70, 0.6], [76, 0.1]),
      'commitment.broken': K([0, 0.05], [66, 0.05], [69, 0.45], [75, 0.05]),
      'order.cadence_ratio': K([0, 0.08], [120, 0.1]),
      'billing.days_past_due': K([0, 0.05], [120, 0.05]),
    },
  },
  {
    id: 'cust-0019', name: 'Corvel Foods', short: 'Corvel', bu: 'Consumer',
    archetype: 'healthy', churnDay: null,
    volume: { email: 0.7, zendesk: 0.2, teams: 0.3, logs: 0.1, erp: 0.55 },
    inputs: {
      'email.time_to_first_response': K([0, 0.1], [60, 0.18], [120, 0.1]),
      'email.open_unanswered_count': K([0, 0.1], [120, 0.12]),
      'email.sentiment': K([0, 0.1], [50, 0.2], [120, 0.1]),
      'email.urgency': K([0, 0.05], [120, 0.05]),
      'ticket.sla_breaches': K([0, 0.05], [40, 0.15], [55, 0.05], [120, 0.05]),
      'commitment.broken': K([0, 0.05], [120, 0.05]),
      'order.cadence_ratio': K([0, 0.1], [120, 0.1]),
      'billing.days_past_due': K([0, 0.05], [120, 0.08]),
    },
  },
  {
    id: 'cust-0031', name: 'Delmar Freight', short: 'Delmar', bu: 'Logistics',
    archetype: 'overt churner', churnDay: 96,
    volume: { email: 1, zendesk: 0.5, teams: 0.2, logs: 0.1, erp: 0.4 },
    inputs: {
      'email.time_to_first_response': K([0, 0.15], [40, 0.2], [70, 0.55]),
      'email.open_unanswered_count': K([0, 0.1], [40, 0.15], [70, 0.5]),
      'email.sentiment': K([0, 0.2], [38, 0.25], [52, 0.9], [120, 0.9]),
      'email.urgency': K([0, 0.05], [38, 0.05], [42, 1], [120, 1]),
      'ticket.sla_breaches': K([0, 0.05], [40, 0.1], [60, 0.7]),
      'commitment.broken': K([0, 0.05], [50, 0.1], [55, 1], [120, 1]),
      'order.cadence_ratio': K([0, 0.1], [55, 0.15], [75, 0.6], [96, 1]),
      'billing.days_past_due': K([0, 0.05], [55, 0.1], [65, 0.8]),
    },
  },
];

export function valueAt(points, day) {
  if (day <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i++) {
    const [d1, v1] = points[i];
    if (day <= d1) {
      const [d0, v0] = points[i - 1];
      return v0 + ((v1 - v0) * (day - d0)) / (d1 - d0);
    }
  }
  return points[points.length - 1][1];
}

export function scoreOf(values) {
  let s = 0;
  for (const inp of INPUTS) s += inp.w * (values[inp.key] ?? 0);
  return Math.round(s * 1000) / 10;
}

export function contributions(values) {
  return INPUTS.map((inp) => ({ ...inp, points: Math.round(inp.w * (values[inp.key] ?? 0) * 1000) / 10 }))
    .sort((a, b) => b.points - a.points);
}

// Risk bands of the 0-100 score, higher is more at risk (docs/yocho-public-api.md).
export function band(score) {
  if (score < 20) return 'healthy';
  if (score < 40) return 'low';
  if (score < 60) return 'moderate';
  if (score < 80) return 'high';
  return 'critical';
}

export function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
